import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../components/Icon';
import { Wordmark } from '../../components/Logo';
import { useT } from '../../lib/i18n';

/**
 * The frame around every sign-in and sign-up screen.
 *
 * Half the width says what the product is and offers the way back to the site,
 * so a person who landed here by mistake is not stuck with the browser's back
 * button; the other half is the form. Under 900px the panel folds into a short
 * header, because a phone has no room for a column of prose beside a form.
 */
export function AuthLayout({ children, title, lede }: { children: ReactNode; title: string; lede?: string }) {
  const t = useT();
  const points = [t('auth.point1'), t('auth.point2'), t('auth.point3')];

  return (
    <div className="flex min-h-[100dvh] bg-canvas">
      <aside className="hidden w-1/2 flex-col justify-between border-r border-line bg-paper p-10 lg:flex">
        <div className="flex items-center justify-between">
          <Link to="/" aria-label={t('app.name')} className="text-ink">
            <Wordmark height={22} />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-meta text-muted transition-colors duration-base ease-emphasis hover:text-ink"
          >
            <Icon name="chevronLeft" size={14} />
            {t('auth.backHome')}
          </Link>
        </div>

        <div className="flex max-w-[440px] flex-col gap-6">
          <h1 className="text-balance text-[34px] font-semibold leading-[1.1] tracking-[-0.03em] text-ink">
            {t('auth.pitch')}
          </h1>
          <ul className="flex flex-col gap-2.5">
            {points.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-body text-muted">
                <Icon name="check" size={14} className="mt-[3px] shrink-0 text-ink" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-4 text-meta-sm text-faint">
          <Link to="/features" className="hover:text-ink">{t('landing.navFeatures')}</Link>
          <Link to="/privacy" className="hover:text-ink">{t('landing.privacy')}</Link>
          <span>{t('landing.copyright')}</span>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between px-5 pt-5 lg:hidden">
          <Link to="/" aria-label={t('app.name')} className="text-ink">
            <Wordmark height={20} />
          </Link>
          <Link to="/" className="flex items-center gap-1 text-meta text-muted hover:text-ink">
            <Icon name="chevronLeft" size={14} />
            {t('auth.backHome')}
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center p-5">
          <div className="w-full max-w-[400px]">
            <h2 className="text-[24px] font-semibold tracking-[-0.025em] text-ink">{title}</h2>
            {lede && <p className="mt-1.5 text-pretty text-meta text-muted">{lede}</p>}
            <div className="mt-7">{children}</div>
          </div>
        </div>
      </main>
    </div>
  );
}
